import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Tag, Plus, Search, Loader2, Copy, Pencil, Trash2, CheckCircle, XCircle, TicketPercent
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const planLabels: Record<string, string> = {
  basic: "أساسي", standard: "متوسط", premium: "متقدم",
};

const AdminPromoCodes = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState("all");

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [form, setForm] = useState({
    code: "",
    discount_percent: "10",
    discount_type: "percentage",
    max_uses: "",
    valid_until: "",
    applicable_plans: [] as string[],
    notes: "",
    is_active: true,
  });

  // Edit
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(form);

  // Delete
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: promoCodes, isLoading } = useQuery({
    queryKey: ["admin-promo-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("promo_codes")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const stats = useMemo(() => {
    if (!promoCodes) return { total: 0, active: 0, totalUses: 0, expired: 0 };
    const now = new Date();
    return {
      total: promoCodes.length,
      active: promoCodes.filter((p: any) => p.is_active && (!p.valid_until || new Date(p.valid_until) > now)).length,
      totalUses: promoCodes.reduce((s: number, p: any) => s + (p.current_uses || 0), 0),
      expired: promoCodes.filter((p: any) => p.valid_until && new Date(p.valid_until) <= now).length,
    };
  }, [promoCodes]);

  const filtered = useMemo(() => {
    if (!promoCodes) return [];
    return promoCodes.filter((p: any) => {
      if (searchTerm && !p.code.toLowerCase().includes(searchTerm.toLowerCase()) && !(p.notes || "").includes(searchTerm)) return false;
      if (filterActive === "active" && !p.is_active) return false;
      if (filterActive === "inactive" && p.is_active) return false;
      return true;
    });
  }, [promoCodes, searchTerm, filterActive]);

  const generateCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "NUR-";
    for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
    return code;
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim()) return;
    setAddLoading(true);

    const { error } = await supabase.from("promo_codes").insert({
      code: form.code.toUpperCase().trim(),
      discount_percent: parseFloat(form.discount_percent) || 10,
      discount_type: form.discount_type,
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      valid_until: form.valid_until || null,
      applicable_plans: form.applicable_plans.length > 0 ? form.applicable_plans : null,
      notes: form.notes || null,
      is_active: form.is_active,
      created_by: user?.id,
    } as any);

    setAddLoading(false);
    if (error) {
      if (error.message.includes("duplicate")) toast.error("هذا الكود موجود مسبقاً");
      else toast.error("خطأ: " + error.message);
    } else {
      toast.success("تم إنشاء الكود الترويجي بنجاح ✨");
      queryClient.invalidateQueries({ queryKey: ["admin-promo-codes"] });
      setAddOpen(false);
      resetForm();
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    setEditLoading(true);

    const { error } = await supabase.from("promo_codes").update({
      code: editForm.code.toUpperCase().trim(),
      discount_percent: parseFloat(editForm.discount_percent) || 10,
      discount_type: editForm.discount_type,
      max_uses: editForm.max_uses ? parseInt(editForm.max_uses) : null,
      valid_until: editForm.valid_until || null,
      applicable_plans: editForm.applicable_plans.length > 0 ? editForm.applicable_plans : null,
      notes: editForm.notes || null,
      is_active: editForm.is_active,
    } as any).eq("id", editId);

    setEditLoading(false);
    if (error) toast.error("خطأ: " + error.message);
    else {
      toast.success("تم تحديث الكود");
      queryClient.invalidateQueries({ queryKey: ["admin-promo-codes"] });
      setEditOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from("promo_codes").delete().eq("id", deleteId);
    if (error) toast.error("خطأ: " + error.message);
    else {
      toast.success("تم حذف الكود");
      queryClient.invalidateQueries({ queryKey: ["admin-promo-codes"] });
    }
    setDeleteId(null);
  };

  const toggleActive = async (id: string, current: boolean) => {
    const { error } = await supabase.from("promo_codes").update({ is_active: !current } as any).eq("id", id);
    if (error) toast.error("خطأ");
    else {
      queryClient.invalidateQueries({ queryKey: ["admin-promo-codes"] });
      toast.success(!current ? "تم تفعيل الكود" : "تم تعطيل الكود");
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success("تم نسخ الكود: " + code);
  };

  const openEdit = (p: any) => {
    setEditId(p.id);
    setEditForm({
      code: p.code,
      discount_percent: String(p.discount_percent),
      discount_type: p.discount_type,
      max_uses: p.max_uses ? String(p.max_uses) : "",
      valid_until: p.valid_until ? p.valid_until.split("T")[0] : "",
      applicable_plans: p.applicable_plans || [],
      notes: p.notes || "",
      is_active: p.is_active,
    });
    setEditOpen(true);
  };

  const resetForm = () => {
    setForm({
      code: "", discount_percent: "10", discount_type: "percentage",
      max_uses: "", valid_until: "", applicable_plans: [], notes: "", is_active: true,
    });
  };

  const togglePlan = (plans: string[], plan: string) =>
    plans.includes(plan) ? plans.filter((p) => p !== plan) : [...plans, plan];

  const PromoForm = ({ f, setF, onSubmit, loading, submitLabel }: any) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>الكود *</Label>
          <div className="flex gap-2">
            <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} required placeholder="NUR-XXXX" dir="ltr" className="font-mono" />
            <Button type="button" variant="outline" size="icon" onClick={() => setF({ ...f, code: generateCode() })}>
              <Tag className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div>
          <Label>نسبة الخصم (%)</Label>
          <Input type="number" value={f.discount_percent} onChange={(e) => setF({ ...f, discount_percent: e.target.value })} min={1} max={100} dir="ltr" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>الحد الأقصى للاستخدام</Label>
          <Input type="number" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })} placeholder="غير محدود" min={1} dir="ltr" />
        </div>
        <div>
          <Label>تاريخ الانتهاء</Label>
          <Input type="date" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} dir="ltr" />
        </div>
      </div>

      <div>
        <Label>الخطط المسموح بها</Label>
        <div className="flex gap-2 mt-1">
          {Object.entries(planLabels).map(([key, label]) => (
            <Badge
              key={key}
              variant={f.applicable_plans.includes(key) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setF({ ...f, applicable_plans: togglePlan(f.applicable_plans, key) })}
            >
              {label}
            </Badge>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">اتركها فارغة للتطبيق على جميع الخطط</p>
      </div>

      <div>
        <Label>ملاحظات</Label>
        <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} placeholder="وصف الكود أو العرض..." />
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <Label>مفعّل</Label>
        <Switch checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} />
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : submitLabel}
      </Button>
    </form>
  );

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">الأكواد الترويجية</h1>
          <p className="text-muted-foreground text-sm">إدارة أكواد الخصم والعروض الخاصة على الاشتراكات</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-l from-amber-500 to-orange-600 border-0 text-white">
              <Plus className="w-4 h-4 ml-2" />
              كود جديد
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" dir="rtl">
            <DialogHeader><DialogTitle>إنشاء كود ترويجي</DialogTitle></DialogHeader>
            <PromoForm f={form} setF={setForm} onSubmit={handleAdd} loading={addLoading} submitLabel="إنشاء الكود" />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><TicketPercent className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي الأكواد</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><CheckCircle className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">مفعّلة</p>
              <p className="text-2xl font-bold">{stats.active}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10"><Tag className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي الاستخدامات</p>
              <p className="text-2xl font-bold">{stats.totalUses}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10"><XCircle className="w-5 h-5 text-red-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">منتهية</p>
              <p className="text-2xl font-bold">{stats.expired}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث بالكود أو الملاحظات..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pr-9" />
            </div>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                <SelectItem value="active">مفعّل</SelectItem>
                <SelectItem value="inactive">معطّل</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TicketPercent className="w-5 h-5" />
            الأكواد ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>الكود</TableHead>
                  <TableHead>الخصم</TableHead>
                  <TableHead>الاستخدام</TableHead>
                  <TableHead>الخطط</TableHead>
                  <TableHead>الصلاحية</TableHead>
                  <TableHead>الحالة</TableHead>
                  <TableHead>إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p: any) => {
                  const isExpired = p.valid_until && new Date(p.valid_until) < new Date();
                  const isMaxed = p.max_uses && p.current_uses >= p.max_uses;
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="font-mono font-bold text-primary bg-primary/5 px-2 py-1 rounded">{p.code}</code>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyCode(p.code)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-bold text-green-600">{p.discount_percent}%</span>
                      </TableCell>
                      <TableCell>
                        <span>{p.current_uses}</span>
                        {p.max_uses && <span className="text-muted-foreground"> / {p.max_uses}</span>}
                        {isMaxed && <Badge variant="destructive" className="mr-1 text-xs">مكتمل</Badge>}
                      </TableCell>
                      <TableCell>
                        {p.applicable_plans?.length > 0
                          ? p.applicable_plans.map((pl: string) => (
                              <Badge key={pl} variant="outline" className="ml-1 text-xs">{planLabels[pl] || pl}</Badge>
                            ))
                          : <span className="text-xs text-muted-foreground">جميع الخطط</span>
                        }
                      </TableCell>
                      <TableCell className="text-xs">
                        {p.valid_until
                          ? <span className={isExpired ? "text-destructive" : ""}>{format(new Date(p.valid_until), "dd/MM/yyyy")}</span>
                          : <span className="text-muted-foreground">مفتوح</span>
                        }
                      </TableCell>
                      <TableCell>
                        <Switch checked={p.is_active && !isExpired} onCheckedChange={() => toggleActive(p.id, p.is_active)} />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { setDeleteId(p.id); }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!filtered.length && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">لا توجد أكواد ترويجية</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader><DialogTitle>تعديل الكود الترويجي</DialogTitle></DialogHeader>
          <PromoForm f={editForm} setF={setEditForm} onSubmit={handleEdit} loading={editLoading} submitLabel="حفظ التعديلات" />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="sm:max-w-sm" dir="rtl">
          <DialogHeader><DialogTitle>حذف الكود الترويجي</DialogTitle></DialogHeader>
          <p className="text-muted-foreground">هل أنت متأكد من حذف هذا الكود؟ لا يمكن التراجع عن هذا الإجراء.</p>
          <div className="flex gap-2 mt-4">
            <Button variant="destructive" className="flex-1" onClick={handleDelete}>حذف</Button>
            <Button variant="outline" className="flex-1" onClick={() => setDeleteId(null)}>إلغاء</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromoCodes;
